import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getApiBaseUrl, publicMediaSrc } from "../config/api";
import CategoryCard, { productHref, MEDIA_CARD_CLASS, MEDIA_CARD_GUTTER, MEDIA_CARD_GRID } from "../components/CategoryCard";
import CategoryDetailSeo from "../components/CategoryDetailSeo";
import { trackPublicPageview } from "../utils/trackPageview";
import NotFoundPage from "./NotFoundPage";
import categoryIndividualBg from "../assets/category-individual-bg.jpg";

const PAGE_GUTTER = "px-16 md:px-32 lg:px-40";
const PAGE_SIZE = 8;

function lockDoubleClick(event) {
  const el = event.currentTarget;
  if (el.dataset.navLock === "1") {
    event.preventDefault();
    return;
  }
  el.dataset.navLock = "1";
  window.setTimeout(() => {
    delete el.dataset.navLock;
  }, 700);
}

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function categoryIdOf(item) {
  return String(item?.category?._id || item?.category || "");
}

function matchesProduct(item, needle) {
  const name = String(item.productName || "").toLowerCase();
  const description = String(item.description || "").toLowerCase();
  const brand = String(item.brand || "").toLowerCase();
  const sku = String(item.sku || "").toLowerCase();
  const modelNumber = String(item.modelNumber || "").toLowerCase();
  const tags = Array.isArray(item.searchTags) ? item.searchTags.join(" ").toLowerCase() : "";
  return (
    name.includes(needle) ||
    description.includes(needle) ||
    brand.includes(needle) ||
    sku.includes(needle) ||
    modelNumber.includes(needle) ||
    tags.includes(needle)
  );
}

function sortProducts(list, sort) {
  const next = [...list];
  if (sort === "oldest") {
    return next.sort((a, b) => new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0));
  }
  if (sort === "name-az") {
    return next.sort((a, b) => String(a.productName || "").localeCompare(String(b.productName || "")));
  }
  if (sort === "name-za") {
    return next.sort((a, b) => String(b.productName || "").localeCompare(String(a.productName || "")));
  }
  return next.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
}

function CategoryDetailPage() {
  const { slug } = useParams();
  const headingRef = useRef(null);
  const pageRef = useRef(null);
  const skipHeadingScroll = useRef(true);
  const prevSlugRef = useRef(slug);
  if (prevSlugRef.current !== slug) {
    prevSlugRef.current = slug;
    skipHeadingScroll.current = true;
  }
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [contact, setContact] = useState(null);
  const [categoryStatus, setCategoryStatus] = useState("loading");
  const [productStatus, setProductStatus] = useState("loading");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [pageBusy, setPageBusy] = useState(false);
  const [loadNonce, setLoadNonce] = useState(0);

  useEffect(() => {
    setCategory(null);
    setProducts([]);
    setCategoryStatus("loading");
    setProductStatus("loading");
    setQuery("");
    setSort("newest");
    setPage(1);
    setPageBusy(false);
    skipHeadingScroll.current = true;
    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (!cancelled) setContact(Array.isArray(list) && list[0] ? list[0] : null);
      })
      .catch(() => {
        if (!cancelled) setContact(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCategory(null);
    setProducts([]);
    setCategoryStatus("loading");
    setProductStatus("loading");

    const categoryFetch = fetch(`${getApiBaseUrl()}/categories`, { headers: { Accept: "application/json" } }).then(
      (res) => {
        if (!res.ok) throw new Error("Unable to load categories");
        return res.json();
      }
    );
    const productFetch = fetch(`${getApiBaseUrl()}/products`, { headers: { Accept: "application/json" } }).then((res) => {
      if (!res.ok) throw new Error("Unable to load products");
      return res.json();
    });

    categoryFetch
      .then((list) => {
        if (cancelled) return;
        const match = (Array.isArray(list) ? list : []).find(
          (item) => item?.isPublished !== false && item?.slug === slug
        );
        if (!match) {
          setCategory(null);
          setCategoryStatus("missing");
          return;
        }
        setCategory(match);
        setCategoryStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setCategory(null);
          setCategoryStatus("error");
        }
      });

    Promise.allSettled([categoryFetch, productFetch]).then(([categoryResult, productResult]) => {
      if (cancelled) return;
      if (categoryResult.status !== "fulfilled") return;
      const match = (Array.isArray(categoryResult.value) ? categoryResult.value : []).find(
        (item) => item?.isPublished !== false && item?.slug === slug
      );
      if (!match) return;
      if (productResult.status !== "fulfilled") {
        setProducts([]);
        setProductStatus("error");
        return;
      }
      const id = String(match._id);
      const inCategory = (Array.isArray(productResult.value) ? productResult.value : []).filter(
        (item) => item?.isPublished !== false && categoryIdOf(item) === id
      );
      setProducts(inCategory);
      setProductStatus("ready");
    });

    return () => {
      cancelled = true;
    };
  }, [slug, loadNonce]);

  useEffect(() => {
    if (categoryStatus !== "ready" || !category?.slug) return;
    trackPublicPageview({
      path: `/category/${category.slug}`,
      title: String(category.categoryName || "").trim(),
    });
  }, [categoryStatus, category?.slug, category?.categoryName]);

  const retryCategory = useCallback(() => {
    setLoadNonce((value) => value + 1);
  }, []);

  const retryProducts = useCallback(() => {
    if (!category?._id) return;
    const id = String(category._id);
    setProductStatus("loading");
    fetch(`${getApiBaseUrl()}/products`, { headers: { Accept: "application/json" } })
      .then((res) => {
        if (!res.ok) throw new Error("Unable to load products");
        return res.json();
      })
      .then((list) => {
        const inCategory = (Array.isArray(list) ? list : []).filter(
          (item) => item?.isPublished !== false && categoryIdOf(item) === id
        );
        setProducts(inCategory);
        setProductStatus("ready");
      })
      .catch(() => {
        setProducts([]);
        setProductStatus("error");
      });
  }, [category]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matched = needle ? products.filter((item) => matchesProduct(item, needle)) : products;
    return sortProducts(matched, sort);
  }, [products, query, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const zeroProducts = productStatus === "ready" && products.length === 0;
  const noSearchResults = productStatus === "ready" && products.length > 0 && filtered.length === 0;

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  useEffect(() => {
    if (skipHeadingScroll.current) {
      skipHeadingScroll.current = false;
      return undefined;
    }
    const node = headingRef.current;
    if (!node) return undefined;
    node.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
    const timer = window.setTimeout(() => setPageBusy(false), 350);
    return () => window.clearTimeout(timer);
  }, [currentPage, slug]);

  useEffect(() => {
    const root = pageRef.current;
    const nodes = root ? root.querySelectorAll("[data-reveal]") : [];
    if (!nodes.length) return undefined;
    if (prefersReducedMotion()) {
      nodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -6% 0px" }
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [visible.length, currentPage, productStatus]);

  const goToPage = (next) => {
    if (pageBusy || next === currentPage || next < 1 || next > pageCount) return;
    setPageBusy(true);
    setPage(next);
  };

  const name = String(category?.categoryName || "").trim();
  const description = String(category?.description || "").trim();
  const liveMessage =
    categoryStatus === "loading" || productStatus === "loading"
      ? "Loading category and products"
      : categoryStatus === "error"
        ? "Unable to load category"
        : productStatus === "error"
          ? "Unable to load products"
          : productStatus === "ready"
            ? `${filtered.length} products`
            : "";

  if (categoryStatus === "missing") {
    return <NotFoundPage />;
  }

  return (
    <div className="category-detail-page" ref={pageRef}>
      {categoryStatus === "ready" ? (
        <CategoryDetailSeo
          contact={contact}
          category={category}
          products={productStatus === "ready" ? products : []}
        />
      ) : null}
      <div className="sr-only" aria-live="polite">
        {liveMessage}
      </div>

      <section className="relative overflow-hidden bg-at-dark text-white">
        <img
          key={slug}
          src={categoryIndividualBg}
          alt=""
          className="hero-zoom-subtle pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="pointer-events-none absolute inset-y-0 left-0 h-full w-full bg-black/55 md:w-[48%]" />
        <div className="pointer-events-none absolute inset-y-0 left-[48%] hidden h-full w-24 bg-gradient-to-r from-black/55 to-transparent md:block" />
        <div className="absolute bottom-5 right-16 z-10 hidden border-l-2 border-at-gold bg-black/90 px-5 py-3 text-left md:right-32 md:block lg:right-40">
          <p className="text-[10px] font-semibold uppercase leading-relaxed tracking-[2px] text-white/90">Built for</p>
          <p className="text-[10px] font-semibold uppercase leading-relaxed tracking-[2px] text-white/90">A Stronger</p>
          <p className="text-[10px] font-semibold uppercase leading-relaxed tracking-[2px] text-white/90">Tomorrow</p>
        </div>
        <div className={`relative z-10 flex min-h-[260px] flex-col justify-between py-10 md:min-h-[320px] md:py-12 ${PAGE_GUTTER}`}>
            <nav key={`${slug}-crumb`} aria-label="Breadcrumb" className="hero-in hero-in-1 text-xs text-white/70">
              <Link to="/" className="hover:text-white">
                Home
              </Link>
              <span className="px-2 text-white/40">/</span>
              <Link to="/category" className="hover:text-white">
                Product Categories
              </Link>
              {categoryStatus === "loading" ? (
                <>
                  <span className="px-2 text-white/40">/</span>
                  <span className="inline-block h-[1em] w-[12ch] bg-white/20 align-middle" aria-hidden="true" />
                </>
              ) : name ? (
                <>
                  <span className="px-2 text-white/40">/</span>
                  <span className="text-white">{name}</span>
                </>
              ) : null}
            </nav>
            <div key={`${slug}-copy`} className="max-w-xl">
              <p className="hero-in hero-in-1 mt-6 text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold">
                Product Category
              </p>
              {categoryStatus === "error" ? (
                <>
                  <h1 className="hero-in hero-in-1 mt-2 font-serif text-3xl uppercase leading-tight md:text-5xl">
                    Unable to load category
                  </h1>
                  <p className="hero-in hero-in-2 mt-3 max-w-md text-sm leading-relaxed text-white/70">
                    This category could not be loaded. Please try again.
                  </p>
                  <button
                    type="button"
                    onClick={(event) => {
                      lockDoubleClick(event);
                      retryCategory();
                    }}
                    className="hero-btn mt-6 inline-flex items-center gap-2 rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
                  >
                    Retry <span className="btn-arrow">→</span>
                  </button>
                </>
              ) : (
                <>
                  <h1 className="hero-in hero-in-1 mt-2 font-serif text-3xl uppercase leading-tight md:text-5xl">
                    {categoryStatus === "loading" ? (
                      <span className="inline-block h-[1em] w-[14ch] bg-white/20 align-middle" aria-hidden="true" />
                    ) : (
                      name || "Category"
                    )}
                  </h1>
                  {categoryStatus === "loading" ? (
                    <p className="hero-in hero-in-2 mt-3 max-w-md text-sm leading-relaxed text-white/70" aria-hidden="true">
                      <span className="block h-[1.25em] bg-white/15" />
                      <span className="mt-2 block h-[1.25em] w-4/5 bg-white/15" />
                    </p>
                  ) : description ? (
                    <p className="hero-in hero-in-2 mt-3 max-w-md text-sm leading-relaxed text-white/70">{description}</p>
                  ) : null}
                  <p className="hero-in hero-in-3 mt-6 text-[10px] font-semibold uppercase leading-relaxed tracking-[0.18em] text-white/70">
                    Stronger Materials
                    <span className="mt-1 block">Brighter Tomorrows</span>
                  </p>
                </>
              )}
            </div>
        </div>
      </section>

      {categoryStatus === "error" ? null : (
        <section className="bg-white py-14">
          <div className={MEDIA_CARD_GUTTER}>
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2
                  ref={headingRef}
                  className="scroll-mt-24 font-serif text-3xl uppercase text-at-dark md:text-4xl"
                >
                  {name || "Products"}
                </h2>
                <p className="mt-2 text-sm text-neutral-500" aria-live="polite">
                  {productStatus === "loading" ? "Loading products…" : `${filtered.length} products`}
                </p>
              </div>
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center lg:w-auto">
                <label className="relative block w-full sm:w-64">
                  <span className="sr-only">Search products</span>
                  <svg
                    viewBox="0 0 24 24"
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20 20l-3-3" />
                  </svg>
                  <input
                    type="search"
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setPage(1);
                    }}
                    placeholder="Search products?"
                    className={`category-search w-full rounded-full border border-neutral-200 bg-white py-2.5 pl-10 text-sm text-at-dark outline-none placeholder:text-neutral-400 focus:border-at-gold ${query ? "pr-10" : "pr-4"}`}
                  />
                  {query ? (
                    <button
                      type="button"
                      aria-label="Clear search"
                      onClick={() => {
                        setQuery("");
                        setPage(1);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-at-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    </button>
                  ) : null}
                </label>
                <label className="relative block w-full sm:w-48">
                  <span className="sr-only">Sort products</span>
                  <select
                    value={sort}
                    onChange={(event) => {
                      setSort(event.target.value);
                      setPage(1);
                    }}
                    className="w-full appearance-none rounded-full border border-neutral-200 bg-white py-2.5 pl-4 pr-10 text-sm text-at-dark outline-none focus:border-at-gold"
                  >
                    <option value="newest">Sort by: Newest</option>
                    <option value="oldest">Sort by: Oldest</option>
                    <option value="name-az">Sort by: Name A–Z</option>
                    <option value="name-za">Sort by: Name Z–A</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="mt-8" aria-busy={productStatus === "loading" || pageBusy}>
              {productStatus === "loading" ? (
                <div className={MEDIA_CARD_GRID} aria-hidden="true">
                  {Array.from({ length: PAGE_SIZE }).map((_, index) => (
                    <div key={index} className={`bg-neutral-200 ${MEDIA_CARD_CLASS}`} />
                  ))}
                </div>
              ) : null}

              {productStatus === "error" ? (
                <div className="text-sm text-neutral-500">
                  <p>Unable to load products.</p>
                  <button
                    type="button"
                    onClick={(event) => {
                      lockDoubleClick(event);
                      retryProducts();
                    }}
                    className="hero-btn mt-3 font-semibold text-at-dark underline decoration-at-gold underline-offset-4 hover:text-at-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
                  >
                    Retry
                  </button>
                </div>
              ) : null}

              {zeroProducts ? (
                <div className="product-results max-w-xl">
                  <h3 className="font-serif text-2xl uppercase text-at-dark">NO PRODUCTS AVAILABLE YET</h3>
                  <p className="mt-3 text-sm text-neutral-500">
                    Products will be added to this category soon. Contact us if you need help sourcing an item.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Link
                      to="/category"
                      onClick={lockDoubleClick}
                      className="hero-btn inline-flex items-center gap-2 rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
                    >
                      Browse Other Categories <span className="btn-arrow">→</span>
                    </Link>
                    <Link
                      to="/contact"
                      state={{ categoryName: name, categorySlug: slug }}
                      onClick={lockDoubleClick}
                      className="hero-btn inline-flex items-center gap-2 rounded-sm border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-at-dark hover:border-at-gold"
                    >
                      Contact Our Team <span className="btn-arrow">→</span>
                    </Link>
                  </div>
                </div>
              ) : null}

              {noSearchResults ? (
                <div className="product-results max-w-xl">
                  <h3 className="font-serif text-2xl uppercase text-at-dark">NO MATCHING PRODUCTS</h3>
                  <p className="mt-3 text-sm text-neutral-500">
                    Try another product name, specification, brand or search term.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={(event) => {
                        lockDoubleClick(event);
                        setQuery("");
                        setPage(1);
                      }}
                      className="hero-btn inline-flex items-center gap-2 rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
                    >
                      Clear Search <span className="btn-arrow">→</span>
                    </button>
                    <Link
                      to="/contact?intent=quote#contact-form"
                      state={{ categoryName: name, categorySlug: slug }}
                      onClick={lockDoubleClick}
                      className="hero-btn inline-flex items-center gap-2 rounded-sm border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-at-dark hover:border-at-gold"
                    >
                      Request a Quote <span className="btn-arrow">→</span>
                    </Link>
                  </div>
                </div>
              ) : null}

              {productStatus === "ready" && visible.length ? (
                <div
                  key={`${query}-${sort}-${currentPage}`}
                  className={`product-results ${MEDIA_CARD_GRID}`}
                >
                  {visible.map((item, index) => (
                    <CategoryCard
                      key={item._id || item.slug}
                      to={productHref(item, slug)}
                      photo={publicMediaSrc(item.image)}
                      alt={item.imageAlt || item.productName || ""}
                      title={item.productName}
                      description={item.brand || name}
                      lazy={index >= 4}
                      revealIndex={index}
                      className={MEDIA_CARD_CLASS}
                    />
                  ))}
                </div>
              ) : null}
            </div>

            {productStatus === "ready" && filtered.length > PAGE_SIZE ? (
              <nav aria-label="Pagination" className="mt-10 flex items-center justify-center gap-3 text-sm text-neutral-500">
                <button
                  type="button"
                  disabled={pageBusy || currentPage <= 1}
                  onClick={() => goToPage(currentPage - 1)}
                  className="disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
                >
                  Previous
                </button>
                {Array.from({ length: pageCount }).map((_, index) => {
                  const number = index + 1;
                  const active = number === currentPage;
                  return (
                    <button
                      key={number}
                      type="button"
                      disabled={pageBusy}
                      aria-current={active ? "page" : undefined}
                      onClick={() => goToPage(number)}
                      className={`flex h-8 w-8 items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold ${active ? "bg-at-gold text-at-dark" : "hover:text-at-dark"}`}
                    >
                      {number}
                    </button>
                  );
                })}
                <button
                  type="button"
                  disabled={pageBusy || currentPage >= pageCount}
                  onClick={() => goToPage(currentPage + 1)}
                  className="disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
                >
                  Next
                </button>
              </nav>
            ) : null}
          </div>
        </section>
      )}
    </div>
  );
}

export default CategoryDetailPage;
